import { action, observable, makeAutoObservable } from 'mobx';
import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import { FooterContactUsElementsState, ContactOptionType } from 'src/main/cms/FooterManagement/FooterManagement';
import { timeout } from 'src_common/common/mobx-utils/timeout';
import { createEmailInputState, createLinkInputState, createPhoneInputState } from './FooterContactUsSingle.utils';
import { FormModel } from 'src_common/common/mobx-utils/Form2/FormModel';
import { TrpcClient } from 'src/AppState/TrpcClient';

interface FormType {
    contact_value: string;
    label: string;
}

export class FooterContactUsSingleElementFormState {
    @observable public errorMessageForList: string | null = null;
    @observable public contactValue: FormInputState<string, string>;
    @observable public contactLabel: FormInputState<string, string>;

    private readonly form: FormModel<FormType>;

    public constructor(
        private contactUsState: FooterContactUsElementsState,
        private contactType: ContactOptionType,
        private readonly trpcClient: TrpcClient
    ) {
        const defaultContactValue = '';
        switch (this.contactType) {
            case 'e-mail':
                this.contactValue = createEmailInputState(defaultContactValue);
                break;
            case 'phone-number':
                this.contactValue = createPhoneInputState(defaultContactValue);
                break;
            case 'twitter':
            case 'instagram':
            case 'facebook':
                this.contactValue = createLinkInputState(defaultContactValue);
                break;
            default:
                this.contactValue = FormInputState.new(defaultContactValue);
        }
        this.contactLabel = FormInputState.new('');

        this.form = FormModel.group({
            contact_value: this.contactValue,
            label: this.contactLabel,
        });
        makeAutoObservable(this);
    }

    @action private resetForm = (): void => {
        this.contactLabel.reset();
        this.contactValue.reset();
    };

    @action private handleError = async (message: string): Promise<void> => {
        this.errorMessageForList = message;
        await timeout(5000);
        this.errorMessageForList = null;
    };

    @action public handleCreate = async (): Promise<void> => {
        await this.contactUsState.contactUsElementsResource.refresh();
        const topElem = this.contactUsState.contactUsElementsData.pop();
        const display_order = topElem === undefined ? 1 : topElem.display_order + 1;

        this.form.setAsVisited();

        /**
         * it's possible to create last element within limit on other tab so this checks again if it is possible to create new element
         */
        const contactTypeAvailable = this.contactUsState.availableContactOptions.includes(this.contactType);

        if (this.form.result.value.type === 'ok' && contactTypeAvailable) {
            const contactUsElementsDetailsData = {
                contact_value: this.contactValue.value,
                label: this.contactLabel.value,
                contact_type: this.contactType,
                is_active: true,
                display_order: display_order,
            };

            try {
                const submitResult =
                    await this.trpcClient.client.footerContactElementsRouter.createContactElement.mutate({
                        data: contactUsElementsDetailsData,
                    });
                if (submitResult.body === undefined) {
                    return this.handleError('Something went wrong.');
                } else {
                    await this.contactUsState.contactUsElementsResource.refresh();
                    this.resetForm();
                }
            } catch (error) {
                console.error(error);
            }
        } else {
            await this.contactUsState.contactUsElementsResource.refresh();
            this.resetForm();
            if (!contactTypeAvailable) {
                alert('Contact element type limit reached');
            }
        }
    };
}
